import { API_ROUTES } from '../constants/api'
import {
  ORDER_LIST_LIMIT,
  ORDER_STATUS,
  PAYMENT_STATUS,
  type Order,
  type OrderAddon,
  type OrderBilling,
  type OrderDelivery,
  type OrderItem,
  type OrderStatus,
} from '../constants/orders'
import { api } from './api-client'
import { isOrderStatus, isPaymentStatus, withQuery } from './orders'

type Raw = Record<string, unknown>

function str(value: unknown, fallback = ''): string {
  return value == null ? fallback : String(value)
}

function num(value: unknown, fallback = 0): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function nullableStr(value: unknown): string | null {
  return value == null || value === '' ? null : String(value)
}

function bool(value: unknown, fallback = false): boolean {
  if (typeof value === 'boolean') return value
  if (value === 'true' || value === 1 || value === '1') return true
  if (value === 'false' || value === 0 || value === '0') return false
  return fallback
}

function nullableNum(value: unknown): number | null {
  if (value == null || value === '') return null
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

function mapAddon(raw: Raw): OrderAddon {
  return {
    productId: str(raw.productId),
    name: str(raw.name),
    unitPrice: num(raw.unitPrice),
  }
}

function mapItem(raw: Raw): OrderItem {
  const addons = Array.isArray(raw.addons)
    ? raw.addons.map((item) => mapAddon(item as Raw))
    : []
  return {
    id: str(raw.id),
    productId: nullableStr(raw.productId),
    variantId: nullableStr(raw.variantId),
    quantity: num(raw.quantity, 1),
    unitPrice: num(raw.unitPrice),
    lineTotal: num(raw.lineTotal),
    productName: str(raw.productName),
    productSlug: str(raw.productSlug),
    variantLabel: str(raw.variantLabel),
    wrapperId: nullableStr(raw.wrapperId),
    wrapperLabel: nullableStr(raw.wrapperLabel),
    imageUrl: nullableStr(raw.imageUrl),
    addons,
  }
}

function mapDelivery(raw: Raw): OrderDelivery {
  return {
    recipientName: str(raw.recipientName),
    recipientPhone: str(raw.recipientPhone),
    address: str(raw.address),
    city: str(raw.city),
    pincode: nullableStr(raw.pincode),
    lat: nullableNum(raw.lat),
    lng: nullableNum(raw.lng),
    notes: nullableStr(raw.notes),
  }
}

function mapBilling(raw: Raw, delivery: OrderDelivery): OrderBilling {
  return {
    name: str(raw.name, delivery.recipientName),
    phone: str(raw.phone, delivery.recipientPhone),
    address: str(raw.address, delivery.address),
    city: str(raw.city, delivery.city),
    pincode: nullableStr(raw.pincode) ?? delivery.pincode,
    sameAsShipping: raw.sameAsShipping !== false,
  }
}

export function mapOrder(raw: Raw): Order {
  const status = str(raw.status, ORDER_STATUS.pending)
  const paymentStatus = str(raw.paymentStatus, PAYMENT_STATUS.unpaid)
  const delivery =
    raw.delivery && typeof raw.delivery === 'object'
      ? mapDelivery(raw.delivery as Raw)
      : mapDelivery({})
  const billing =
    raw.billing && typeof raw.billing === 'object'
      ? mapBilling(raw.billing as Raw, delivery)
      : mapBilling({}, delivery)
  const items = Array.isArray(raw.items)
    ? raw.items.map((item) => mapItem(item as Raw))
    : []
  return {
    id: str(raw.id),
    orderNumber: str(raw.orderNumber),
    userId: str(raw.userId),
    status: isOrderStatus(status) ? status : ORDER_STATUS.pending,
    paymentStatus: isPaymentStatus(paymentStatus)
      ? paymentStatus
      : PAYMENT_STATUS.unpaid,
    vendorId: nullableStr(raw.vendorId),
    vendorLocationId: nullableStr(raw.vendorLocationId),
    isExpress: bool(raw.isExpress),
    checkoutId: nullableStr(raw.checkoutId),
    delivery,
    billing,
    items,
    subtotal: num(raw.subtotal),
    discountTotal: num(raw.discountTotal),
    deliveryFee: num(raw.deliveryFee),
    total: num(raw.total),
    cancelReason: nullableStr(raw.cancelReason),
    cancelledAt: nullableStr(raw.cancelledAt),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  }
}

function toRawOrders(raw: unknown): Raw[] {
  if (Array.isArray(raw)) return raw as Raw[]
  if (raw && typeof raw === 'object') {
    const record = raw as { data?: unknown; items?: unknown }
    if (Array.isArray(record.data)) return record.data as Raw[]
    if (Array.isArray(record.items)) return record.items as Raw[]
  }
  return []
}

export async function fetchOrders(input: {
  status?: OrderStatus
  limit?: number
} = {}): Promise<Order[]> {
  const raw = await api.get<unknown>(
    withQuery(API_ROUTES.orders, {
      status: input.status,
      limit: input.limit ?? ORDER_LIST_LIMIT,
    }),
  )
  const rows = toRawOrders(raw).map((item) => mapOrder(item))
  return input.status
    ? rows.filter((order) => order.status === input.status)
    : rows
}

export async function fetchOrder(id: string): Promise<Order> {
  return mapOrder(await api.get<Raw>(API_ROUTES.order(id)))
}
